import {Space} from 'antd';
import {useCallback} from 'react';

import {appRoute} from '../../../../../app-route';
import {Meta} from '../../../../../component/meta/meta';
import {PageHeaderTitle} from '../../../../../component/page-header-title/page-header-title';
import {dateRangeToWorksheetName} from '../../../../../component/request-report-base/request-report-base-helper';
import {UsetifulNameProductEnum} from '../../../../../component/usetiful/usetiful-const';
import {BreadCrumbs} from '../../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../../layout/page/page';
import {PageHeader} from '../../../../../layout/page-header/page-header';
import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {fetchReviewCityList} from '../../../../../service/reivew-analysis/review-cities/review-cities-api';
import {
    ReviewCityListQueryType,
    ReviewCityType,
} from '../../../../../service/reivew-analysis/review-cities/review-cities-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {TimeSizeEnum} from '../../../../../util/format';
import {useFormat} from '../../../../../util/format-hook/format-hook';
import {ActiveFilter} from '../../components/active-filter/active-filter';
import {DrawerFilter} from '../../components/drawer-filter/drawer-filter';
import {RequestReport} from '../../components/request-report/request-report';
import {useReviewsAnalysisFilter} from '../../reviews-analysis-hook';
import {CompareModeResolver} from '../dashboard/compare-mode-resolver/compare-mode-resolver';

import {citiesPageColumns} from './cities-page-helper';
import {CityTableDataType} from './cities-page-type';
import {CitiesTableCompetitor} from './cities-table-competitor/cities-table-competitor';
import {CitiesTablePeriod} from './cities-table-period/cities-table-period';
import {CitiesTableSingle} from './cities-table-single/cities-table-single';

export function CitiesPage(): JSX.Element {
    const {reviewsAnalysisDashboard: dashboardRoute, reviewsAnalysisCities: citiesRoute} = appRoute;

    const {getLocalizedString} = useLocale();
    const {getFormattedNumber} = useFormat();

    const {singleFilter} = useReviewsAnalysisFilter();
    const getDataSource = useCallback((array: Array<ReviewCityType>, isForExport?: boolean) => {
        return array.map<CityTableDataType>(
            (dataItem: ReviewCityType, index): CityTableDataType => ({
                ...dataItem,
                key: `${dataItem.cityId} ${index} ${isForExport && 'export'}`,
            })
        );
    }, []);

    function formatDataForXls(data: Array<ReviewCityType>) {
        return data.map((city) => ({
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__CITY')]: city.cityName,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__RATE')]: city.avgRating.toFixed(1),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEW_TOTAL')]: city.total,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEWS_REPLIED')]: city.replied,
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__WITH_MESSAGE_PERCENTAGE')]: getFormattedNumber(
                city.repliedPercent,
                {style: 'percent'}
            ),
            [getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__REPLY_DELAY')]: city.avgResponseSpeed
                ? getFormattedNumber(city.avgResponseSpeed, {style: 'unit', unit: TimeSizeEnum.minute})
                : '-',
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__POSITIVE'
            )}`]: city.sentiment.positive,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEUTRAL'
            )}`]: city.sentiment.neutral,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__NEGATIVE'
            )}`]: city.sentiment.negative,
            [`${getLocalizedString('REVIEWS_ANALYSIS__TABLE__COLUMN__SENTIMENT')}: ${getLocalizedString(
                'REVIEWS_ANALYSIS__SENTIMENT__UNSPECIFIED'
            )}`]: city.sentiment.unspecified,
        }));
    }

    return (
        <Page>
            <Meta title={getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES')} />

            <BreadCrumbs
                list={[
                    {
                        path: dashboardRoute.path,
                        titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS',
                    },
                    {
                        path: citiesRoute.path,
                        titleLangKey: 'CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES',
                    },
                ]}
            />

            <PageHeader>
                <PageHeaderTitle
                    productName={UsetifulNameProductEnum.REVIEWS_ANALYSIS__CITIES}
                    title="CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES"
                />

                <Space>
                    <RequestReport<CityTableDataType, ReviewCityType, ReviewCityListQueryType>
                        apiHandler={fetchReviewCityList}
                        columns={citiesPageColumns({
                            timeUnit: singleFilter.timeFormat || ReviewsAndAnswersStatisticsUnitMeasurementEnum.Minutes,
                        })}
                        defaultFilename={getLocalizedString('CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES')}
                        filters={{...singleFilter}}
                        formatDataForXls={formatDataForXls}
                        getDataSource={getDataSource}
                        title={<Locale stringKey="CATEGORY_NAME__REVIEWS_ANALYSIS__CITIES" />}
                        xlsxWorksheetName={dateRangeToWorksheetName({
                            rawStartDate: singleFilter.startDate,
                            rawEndDate: singleFilter.endDate,
                        })}
                    />
                    <DrawerFilter isCompareRangeAllowed isCompetitorAllowed />
                </Space>
            </PageHeader>

            <ActiveFilter isCompareRangeAllowed isCompetitorAllowed isPreview={false} isWithTextOnly={false} />

            <CompareModeResolver
                renderCompetitor={<CitiesTableCompetitor />}
                renderPeriod={<CitiesTablePeriod />}
                renderSingle={<CitiesTableSingle />}
            />
        </Page>
    );
}
